/**
 * Comprehensive Role-Based Access Control (RBAC) Permissions
 * Compatible with Symfony security system (ROLE_ prefix)
 * 
 * Structure:
 * - ROLES: High-level roles for admin users
 * - PERMISSIONS: Granular permissions for specific actions
 * - ROLE_GROUPS: Organized by module for UI display
 */

// ============================================
// ROLES (Symfony Compatible - ROLE_ prefix)
// ============================================
export const ROLES = {
    // User - Regular user
    USER: 'ROLE_USER',
    // Super Admin - Full access
    MASTER: 'ROLE_MASTER',
    // Supervisor - Can manage most things except system settings
    SUPERVISOR: 'ROLE_SUPERVISOR',
    // Product Manager - Products, categories, discounts
    PRODUCT: 'ROLE_PRODUCT',
    // Media Manager - Media files, catalog
    MEDIA: 'ROLE_MEDIA',
    // Digital Products Manager - Formations, digital content
    DIGITAL: 'ROLE_DIGITAL',
    // Finance Manager - Invoices, payments, financial reports
    FINANCIER: 'ROLE_FINANCIER',
    // Order Manager - Orders, deliveries
    ORDER: 'ROLE_ORDER',
    // Training Manager - Trainings, formations
    TRAINING: 'ROLE_TRAINING',
    // Customer (regular user)
    CUSTOMER: 'ROLE_CUSTOMER',
};

// Role hierarchy (highest to lowest)
export const ROLE_HIERARCHY = [
    ROLES.MASTER,
    ROLES.SUPERVISOR,
    ROLES.TRAINING,
    ROLES.PRODUCT,
    ROLES.DIGITAL,
    ROLES.MEDIA,
    ROLES.ORDER,
    ROLES.FINANCIER,
    ROLES.CUSTOMER
];

// ============================================
// GRANULAR PERMISSIONS (Symfony Compatible)
// ============================================
export const PERMISSIONS = {
    // Country Access Permissions
    COUNTRY: {
        ALL: 'ROLE_COUNTRY_ALL',
        // Dynamic per country: ROLE_COUNTRY_TN, ROLE_COUNTRY_DZ, etc.
    },

    // Dashboard
    DASHBOARD: {
        VIEW: 'ROLE_DASHBOARD_VIEW',
        METABASE: 'ROLE_DASHBOARD_METABASE',
        SALES: 'ROLE_DASHBOARD_SALES',
    },

    // Users Management
    USERS: {
        VIEW: 'ROLE_USERS_VIEW',
        EDIT: 'ROLE_USERS_EDIT',
        BLOCK: 'ROLE_USERS_BLOCK',
        CHANGE_PASSWORD: 'ROLE_USERS_CHANGE_PASSWORD',
        VIEW_DETAILS: 'ROLE_USERS_VIEW_DETAILS',
        EXPORT: 'ROLE_USERS_EXPORT',
        TOOLS: 'ROLE_USERS_TOOLS',
        CHANGE_LEVEL: 'ROLE_CHANGE_USER_LEVEL',
        CHANGE_PARENT: 'ROLE_CHANGE_USER_TREE',
    },

    // Admin Management
    ADMINS: {
        VIEW: 'ROLE_ADMINS_VIEW',
        CREATE: 'ROLE_ADMINS_CREATE',
        EDIT: 'ROLE_ADMINS_EDIT',
        BLOCK: 'ROLE_ADMINS_BLOCK',
        CHANGE_PASSWORD: 'ROLE_ADMINS_CHANGE_PASSWORD',
        DELETE: 'ROLE_ADMINS_DELETE',
        MANAGE_PERMISSIONS: 'ROLE_ADMINS_MANAGE_PERMISSIONS',
    },

    // Products
    PRODUCTS: {
        VIEW: 'ROLE_PRODUCTS_VIEW',
        CREATE: 'ROLE_PRODUCTS_CREATE',
        EDIT: 'ROLE_PRODUCTS_EDIT',
        DELETE: 'ROLE_PRODUCTS_DELETE',
        UPDATE_STOCK: 'ROLE_PRODUCTS_UPDATE_STOCK',
        MANAGE_VISIBILITY: 'ROLE_PRODUCTS_MANAGE_VISIBILITY',
        MANAGE_PRICES: 'ROLE_PRODUCTS_MANAGE_PRICES',
    },

    // Categories
    CATEGORIES: {
        VIEW: 'ROLE_CATEGORIES_VIEW',
        CREATE: 'ROLE_CATEGORIES_CREATE',
        EDIT: 'ROLE_CATEGORIES_EDIT',
        DELETE: 'ROLE_CATEGORIES_DELETE',
    },

    // Formations (Digital Products)
    FORMATIONS: {
        VIEW: 'ROLE_FORMATIONS_VIEW',
        CREATE: 'ROLE_FORMATIONS_CREATE',
        EDIT: 'ROLE_FORMATIONS_EDIT',
        DELETE: 'ROLE_FORMATIONS_DELETE',
        TICKETS_VIEW: 'ROLE_FORMATIONS_TICKETS_VIEW',
        TICKETS_VALIDATE: 'ROLE_FORMATIONS_TICKETS_VALIDATE',
    },

    // Orders
    ORDERS: {
        VIEW: 'ROLE_ORDERS_VIEW',
        VIEW_ITEMS: 'ROLE_ORDERS_VIEW_ITEMS',
        VALIDATE: 'ROLE_ORDERS_VALIDATE',
        EXPORT: 'ROLE_ORDERS_EXPORT',
        VIEW_USER_DETAILS: 'ROLE_ORDERS_VIEW_USER_DETAILS',
        EXPORT_BON_COMMAND: 'ROLE_ORDERS_EXPORT_BON_COMMAND',
    },

    // Invoices
    INVOICES: {
        VIEW: 'ROLE_INVOICES_VIEW',
        CHANGE_STATUS: 'ROLE_INVOICES_CHANGE_STATUS',
        EXPORT: 'ROLE_INVOICES_EXPORT',
        EXPORT_FACTURE: 'ROLE_INVOICES_EXPORT_FACTURE',
        UPLOAD_DELIVERY_PAID: 'ROLE_INVOICES_UPLOAD_DELIVERY_PAID',
        VERIFY_PAYMENT: 'ROLE_INVOICES_VERIFY_PAYMENT',
    },

    // Finance
    FINANCE: {
        VIEW: 'ROLE_FINANCE_VIEW',
        VIEW_TOTALS: 'ROLE_FINANCE_VIEW_TOTALS',
        EXPORT: 'ROLE_FINANCE_EXPORT',
        VIEW_REPORTS: 'ROLE_FINANCE_VIEW_REPORTS',
    },

    // Payments
    PAYMENTS: {
        VIEW: 'ROLE_PAYMENTS_VIEW',
        VIEW_DETAILS: 'ROLE_PAYMENTS_VIEW_DETAILS',
        METHODS_VIEW: 'ROLE_PAYMENTS_METHODS_VIEW',
        METHODS_MANAGE: 'ROLE_PAYMENTS_METHODS_MANAGE',
    },

    // Gift Cards
    GIFT_CARDS: {
        VIEW: 'ROLE_GIFT_CARDS_VIEW',
        CREATE: 'ROLE_GIFT_CARDS_CREATE',
        DEACTIVATE: 'ROLE_GIFT_CARDS_DEACTIVATE',
        EXPORT: 'ROLE_GIFT_CARDS_EXPORT',
    },

    // Discounts
    DISCOUNTS: {
        VIEW: 'ROLE_DISCOUNTS_VIEW',
        CREATE: 'ROLE_DISCOUNTS_CREATE',
        EDIT: 'ROLE_DISCOUNTS_EDIT',
        DELETE: 'ROLE_DISCOUNTS_DELETE',
        TOGGLE: 'ROLE_DISCOUNTS_TOGGLE',
        VIEW_PRODUCTS: 'ROLE_DISCOUNTS_VIEW_PRODUCTS',
        MANAGE_PRODUCTS: 'ROLE_DISCOUNTS_MANAGE_PRODUCTS',
    },

    // Promo
    PROMO: {
        VIEW: 'ROLE_PROMO_VIEW',
        CREATE: 'ROLE_PROMO_CREATE',
        EDIT: 'ROLE_PROMO_EDIT',
        DELETE: 'ROLE_PROMO_DELETE',
        GRATUITY_MANAGE: 'ROLE_PROMO_GRATUITY_MANAGE',
        WELCOME_GIFT_MANAGE: 'ROLE_PROMO_WELCOME_GIFT_MANAGE',
    },

    // Challenges
    CHALLENGES: {
        VIEW: 'ROLE_CHALLENGES_VIEW',
        CREATE: 'ROLE_CHALLENGES_CREATE',
        EDIT: 'ROLE_CHALLENGES_EDIT',
        VIEW_DETAILS: 'ROLE_CHALLENGES_VIEW_DETAILS',
    },

    // Surveys
    SURVEYS: {
        VIEW: 'ROLE_SURVEYS_VIEW',
        CREATE: 'ROLE_SURVEYS_CREATE',
        EDIT: 'ROLE_SURVEYS_EDIT',
        DELETE: 'ROLE_SURVEYS_DELETE',
    },

    // Carriers
    CARRIERS: {
        VIEW: 'ROLE_CARRIERS_VIEW',
        CREATE: 'ROLE_CARRIERS_CREATE',
        EDIT: 'ROLE_CARRIERS_EDIT',
        DELETE: 'ROLE_CARRIERS_DELETE',
        TOGGLE: 'ROLE_CARRIERS_TOGGLE',
    },

    // Providers (SMS/Notifications)
    PROVIDERS: {
        VIEW: 'ROLE_PROVIDERS_VIEW',
        CREATE: 'ROLE_PROVIDERS_CREATE',
        EDIT: 'ROLE_PROVIDERS_EDIT',
        DELETE: 'ROLE_PROVIDERS_DELETE',
        TOGGLE: 'ROLE_PROVIDERS_TOGGLE',
    },

    // Notifications
    NOTIFICATIONS: {
        VIEW: 'ROLE_NOTIFICATIONS_VIEW',
        SEND: 'ROLE_NOTIFICATIONS_SEND',
        MANAGE: 'ROLE_NOTIFICATIONS_MANAGE',
    },

    // Bonus
    BONUS: {
        DASHBOARD: 'ROLE_BONUS_DASHBOARD',
        GENERATION: 'ROLE_BONUS_GENERATION',
        LEVEL: 'ROLE_BONUS_LEVEL',
        DROPSHIPPING: 'ROLE_BONUS_DROPSHIPPING',
        RESTORE: 'ROLE_BONUS_RESTORE',
        COACHING: 'ROLE_BONUS_COACHING',
        EXPORT: 'ROLE_BONUS_EXPORT',
    },

    // Reglement
    REGLEMENT: {
        VIEW: 'ROLE_REGLEMENT_VIEW',
        CREATE: 'ROLE_REGLEMENT_CREATE',
        EXPORT: 'ROLE_REGLEMENT_EXPORT',
        DOWNLOAD: 'ROLE_REGLEMENT_DOWNLOAD',
    },

    // Media
    MEDIA: {
        VIEW: 'ROLE_MEDIA_VIEW',
        UPLOAD: 'ROLE_MEDIA_UPLOAD',
        CATALOG_UPDATER: 'ROLE_MEDIA_CATALOG_UPDATER',
        FILES_MANAGE: 'ROLE_MEDIA_FILES_MANAGE',
        DELETE: 'ROLE_MEDIA_DELETE',
    },

    // Countries Settings
    COUNTRIES_SETTINGS: {
        VIEW: 'ROLE_COUNTRIES_VIEW',
        CREATE: 'ROLE_COUNTRIES_CREATE',
        EDIT: 'ROLE_COUNTRIES_EDIT',
        MANAGE_TAXES: 'ROLE_COUNTRIES_MANAGE_TAXES',
        MANAGE_CURRENCY: 'ROLE_COUNTRIES_MANAGE_CURRENCY',
    },

    // International
    INTERNATIONAL: {
        CURRENCY_VIEW: 'ROLE_INTERNATIONAL_CURRENCY_VIEW',
        CURRENCY_MANAGE: 'ROLE_INTERNATIONAL_CURRENCY_MANAGE',
        LANGUAGE_VIEW: 'ROLE_INTERNATIONAL_LANGUAGE_VIEW',
        LANGUAGE_MANAGE: 'ROLE_INTERNATIONAL_LANGUAGE_MANAGE',
    },

    // Settings
    SETTINGS: {
        VIEW: 'ROLE_SETTINGS_VIEW',
        EDIT: 'ROLE_SETTINGS_EDIT',
        SYSTEM: 'ROLE_SETTINGS_SYSTEM',
    },

    // Logs
    LOGS: {
        VIEW: 'ROLE_LOGS_VIEW',
    },
};

// ============================================
// PERMISSION GROUPS (For UI Display)
// Organized by module with labels and icons
// ============================================
export const ROLE_GROUPS = [
    {
        key: 'dashboard',
        labelKey: 'permissions.group.dashboard',
        icon: 'DashboardOutlined',
        permissions: [
            { value: PERMISSIONS.DASHBOARD.VIEW, labelKey: 'permissions.dashboard.view' },
            { value: PERMISSIONS.DASHBOARD.METABASE, labelKey: 'permissions.dashboard.metabase' },
            { value: PERMISSIONS.DASHBOARD.SALES, labelKey: 'permissions.dashboard.sales' },
        ]
    },
    {
        key: 'users',
        labelKey: 'permissions.group.users',
        icon: 'UserOutlined',
        permissions: [
            { value: PERMISSIONS.USERS.VIEW, labelKey: 'permissions.users.view' },
            { value: PERMISSIONS.USERS.EDIT, labelKey: 'permissions.users.edit' },
            { value: PERMISSIONS.USERS.BLOCK, labelKey: 'permissions.users.block' },
            { value: PERMISSIONS.USERS.CHANGE_PASSWORD, labelKey: 'permissions.users.change_password' },
            { value: PERMISSIONS.USERS.VIEW_DETAILS, labelKey: 'permissions.users.view_details' },
            { value: PERMISSIONS.USERS.EXPORT, labelKey: 'permissions.users.export' },
        ]
    },
    {
        key: 'admins',
        labelKey: 'permissions.group.admins',
        icon: 'TeamOutlined',
        permissions: [
            { value: PERMISSIONS.ADMINS.VIEW, labelKey: 'permissions.admins.view' },
            { value: PERMISSIONS.ADMINS.CREATE, labelKey: 'permissions.admins.create' },
            { value: PERMISSIONS.ADMINS.EDIT, labelKey: 'permissions.admins.edit' },
            { value: PERMISSIONS.ADMINS.BLOCK, labelKey: 'permissions.admins.block' },
            { value: PERMISSIONS.ADMINS.CHANGE_PASSWORD, labelKey: 'permissions.admins.change_password' },
            { value: PERMISSIONS.ADMINS.DELETE, labelKey: 'permissions.admins.delete' },
            { value: PERMISSIONS.ADMINS.MANAGE_PERMISSIONS, labelKey: 'permissions.admins.manage_permissions' },
        ]
    },
    {
        key: 'products',
        labelKey: 'permissions.group.products',
        icon: 'ShoppingOutlined',
        permissions: [
            { value: PERMISSIONS.PRODUCTS.VIEW, labelKey: 'permissions.products.view' },
            { value: PERMISSIONS.PRODUCTS.CREATE, labelKey: 'permissions.products.create' },
            { value: PERMISSIONS.PRODUCTS.EDIT, labelKey: 'permissions.products.edit' },
            { value: PERMISSIONS.PRODUCTS.DELETE, labelKey: 'permissions.products.delete' },
            { value: PERMISSIONS.PRODUCTS.UPDATE_STOCK, labelKey: 'permissions.products.update_stock' },
            { value: PERMISSIONS.PRODUCTS.MANAGE_VISIBILITY, labelKey: 'permissions.products.manage_visibility' },
            { value: PERMISSIONS.PRODUCTS.MANAGE_PRICES, labelKey: 'permissions.products.manage_prices' },
        ]
    },
    {
        key: 'categories',
        labelKey: 'permissions.group.categories',
        icon: 'AppstoreOutlined',
        permissions: [
            { value: PERMISSIONS.CATEGORIES.VIEW, labelKey: 'permissions.categories.view' },
            { value: PERMISSIONS.CATEGORIES.CREATE, labelKey: 'permissions.categories.create' },
            { value: PERMISSIONS.CATEGORIES.EDIT, labelKey: 'permissions.categories.edit' },
            { value: PERMISSIONS.CATEGORIES.DELETE, labelKey: 'permissions.categories.delete' },
        ]
    },
    {
        key: 'formations',
        labelKey: 'permissions.group.formations',
        icon: 'ReadOutlined',
        permissions: [
            { value: PERMISSIONS.FORMATIONS.VIEW, labelKey: 'permissions.formations.view' },
            { value: PERMISSIONS.FORMATIONS.CREATE, labelKey: 'permissions.formations.create' },
            { value: PERMISSIONS.FORMATIONS.EDIT, labelKey: 'permissions.formations.edit' },
            { value: PERMISSIONS.FORMATIONS.DELETE, labelKey: 'permissions.formations.delete' },
            { value: PERMISSIONS.FORMATIONS.TICKETS_VIEW, labelKey: 'permissions.formations.tickets_view' },
            { value: PERMISSIONS.FORMATIONS.TICKETS_VALIDATE, labelKey: 'permissions.formations.tickets_validate' },
        ]
    },
    {
        key: 'orders',
        labelKey: 'permissions.group.orders',
        icon: 'ShoppingCartOutlined',
        permissions: [
            { value: PERMISSIONS.ORDERS.VIEW, labelKey: 'permissions.orders.view' },
            { value: PERMISSIONS.ORDERS.VIEW_ITEMS, labelKey: 'permissions.orders.view_items' },
            { value: PERMISSIONS.ORDERS.VALIDATE, labelKey: 'permissions.orders.validate' },
            { value: PERMISSIONS.ORDERS.EXPORT, labelKey: 'permissions.orders.export' },
            { value: PERMISSIONS.ORDERS.VIEW_USER_DETAILS, labelKey: 'permissions.orders.view_user_details' },
            { value: PERMISSIONS.ORDERS.EXPORT_BON_COMMAND, labelKey: 'permissions.orders.export_bon_command' },
        ]
    },
    {
        key: 'invoices',
        labelKey: 'permissions.group.invoices',
        icon: 'FileTextOutlined',
        permissions: [
            { value: PERMISSIONS.INVOICES.VIEW, labelKey: 'permissions.invoices.view' },
            { value: PERMISSIONS.INVOICES.CHANGE_STATUS, labelKey: 'permissions.invoices.change_status' },
            { value: PERMISSIONS.INVOICES.EXPORT, labelKey: 'permissions.invoices.export' },
            { value: PERMISSIONS.INVOICES.EXPORT_FACTURE, labelKey: 'permissions.invoices.export_facture' },
            { value: PERMISSIONS.INVOICES.UPLOAD_DELIVERY_PAID, labelKey: 'permissions.invoices.upload_delivery_paid' },
            { value: PERMISSIONS.INVOICES.VERIFY_PAYMENT, labelKey: 'permissions.invoices.verify_payment' },
        ]
    },
    {
        key: 'finance',
        labelKey: 'permissions.group.finance',
        icon: 'DollarOutlined',
        permissions: [
            { value: PERMISSIONS.FINANCE.VIEW, labelKey: 'permissions.finance.view' },
            { value: PERMISSIONS.FINANCE.VIEW_TOTALS, labelKey: 'permissions.finance.view_totals' },
            { value: PERMISSIONS.FINANCE.EXPORT, labelKey: 'permissions.finance.export' },
            { value: PERMISSIONS.FINANCE.VIEW_REPORTS, labelKey: 'permissions.finance.view_reports' },
        ]
    },
    {
        key: 'payments',
        labelKey: 'permissions.group.payments',
        icon: 'CreditCardOutlined',
        permissions: [
            { value: PERMISSIONS.PAYMENTS.VIEW, labelKey: 'permissions.payments.view' },
            { value: PERMISSIONS.PAYMENTS.VIEW_DETAILS, labelKey: 'permissions.payments.view_details' },
            { value: PERMISSIONS.PAYMENTS.METHODS_VIEW, labelKey: 'permissions.payments.methods_view' },
            { value: PERMISSIONS.PAYMENTS.METHODS_MANAGE, labelKey: 'permissions.payments.methods_manage' },
        ]
    },
    {
        key: 'gift_cards',
        labelKey: 'permissions.group.gift_cards',
        icon: 'GiftOutlined',
        permissions: [
            { value: PERMISSIONS.GIFT_CARDS.VIEW, labelKey: 'permissions.gift_cards.view' },
            { value: PERMISSIONS.GIFT_CARDS.CREATE, labelKey: 'permissions.gift_cards.create' },
            { value: PERMISSIONS.GIFT_CARDS.DEACTIVATE, labelKey: 'permissions.gift_cards.deactivate' },
            { value: PERMISSIONS.GIFT_CARDS.EXPORT, labelKey: 'permissions.gift_cards.export' },
        ]
    },
    {
        key: 'discounts',
        labelKey: 'permissions.group.discounts',
        icon: 'TagOutlined',
        permissions: [
            { value: PERMISSIONS.DISCOUNTS.VIEW, labelKey: 'permissions.discounts.view' },
            { value: PERMISSIONS.DISCOUNTS.CREATE, labelKey: 'permissions.discounts.create' },
            { value: PERMISSIONS.DISCOUNTS.EDIT, labelKey: 'permissions.discounts.edit' },
            { value: PERMISSIONS.DISCOUNTS.DELETE, labelKey: 'permissions.discounts.delete' },
            { value: PERMISSIONS.DISCOUNTS.TOGGLE, labelKey: 'permissions.discounts.toggle' },
            { value: PERMISSIONS.DISCOUNTS.VIEW_PRODUCTS, labelKey: 'permissions.discounts.view_products' },
            { value: PERMISSIONS.DISCOUNTS.MANAGE_PRODUCTS, labelKey: 'permissions.discounts.manage_products' },
        ]
    },
    {
        key: 'promo',
        labelKey: 'permissions.group.promo',
        icon: 'FireOutlined',
        permissions: [
            { value: PERMISSIONS.PROMO.VIEW, labelKey: 'permissions.promo.view' },
            { value: PERMISSIONS.PROMO.CREATE, labelKey: 'permissions.promo.create' },
            { value: PERMISSIONS.PROMO.EDIT, labelKey: 'permissions.promo.edit' },
            { value: PERMISSIONS.PROMO.DELETE, labelKey: 'permissions.promo.delete' },
            { value: PERMISSIONS.PROMO.GRATUITY_MANAGE, labelKey: 'permissions.promo.gratuity_manage' },
            { value: PERMISSIONS.PROMO.WELCOME_GIFT_MANAGE, labelKey: 'permissions.promo.welcome_gift_manage' },
        ]
    },
    {
        key: 'challenges',
        labelKey: 'permissions.group.challenges',
        icon: 'TrophyOutlined',
        permissions: [
            { value: PERMISSIONS.CHALLENGES.VIEW, labelKey: 'permissions.challenges.view' },
            { value: PERMISSIONS.CHALLENGES.CREATE, labelKey: 'permissions.challenges.create' },
            { value: PERMISSIONS.CHALLENGES.EDIT, labelKey: 'permissions.challenges.edit' },
            { value: PERMISSIONS.CHALLENGES.VIEW_DETAILS, labelKey: 'permissions.challenges.view_details' },
        ]
    },
    {
        key: 'surveys',
        labelKey: 'permissions.group.surveys',
        icon: 'FormOutlined',
        permissions: [
            { value: PERMISSIONS.SURVEYS.VIEW, labelKey: 'permissions.surveys.view' },
            { value: PERMISSIONS.SURVEYS.CREATE, labelKey: 'permissions.surveys.create' },
            { value: PERMISSIONS.SURVEYS.EDIT, labelKey: 'permissions.surveys.edit' },
            { value: PERMISSIONS.SURVEYS.DELETE, labelKey: 'permissions.surveys.delete' },
        ]
    },
    {
        key: 'carriers',
        labelKey: 'permissions.group.carriers',
        icon: 'CarOutlined',
        permissions: [
            { value: PERMISSIONS.CARRIERS.VIEW, labelKey: 'permissions.carriers.view' },
            { value: PERMISSIONS.CARRIERS.CREATE, labelKey: 'permissions.carriers.create' },
            { value: PERMISSIONS.CARRIERS.EDIT, labelKey: 'permissions.carriers.edit' },
            { value: PERMISSIONS.CARRIERS.DELETE, labelKey: 'permissions.carriers.delete' },
            { value: PERMISSIONS.CARRIERS.TOGGLE, labelKey: 'permissions.carriers.toggle' },
        ]
    },
    {
        key: 'providers',
        labelKey: 'permissions.group.providers',
        icon: 'ApiOutlined',
        permissions: [
            { value: PERMISSIONS.PROVIDERS.VIEW, labelKey: 'permissions.providers.view' },
            { value: PERMISSIONS.PROVIDERS.CREATE, labelKey: 'permissions.providers.create' },
            { value: PERMISSIONS.PROVIDERS.EDIT, labelKey: 'permissions.providers.edit' },
            { value: PERMISSIONS.PROVIDERS.DELETE, labelKey: 'permissions.providers.delete' },
            { value: PERMISSIONS.PROVIDERS.TOGGLE, labelKey: 'permissions.providers.toggle' },
        ]
    },
    {
        key: 'notifications',
        labelKey: 'permissions.group.notifications',
        icon: 'BellOutlined',
        permissions: [
            { value: PERMISSIONS.NOTIFICATIONS.VIEW, labelKey: 'permissions.notifications.view' },
            { value: PERMISSIONS.NOTIFICATIONS.SEND, labelKey: 'permissions.notifications.send' },
            { value: PERMISSIONS.NOTIFICATIONS.MANAGE, labelKey: 'permissions.notifications.manage' },
        ]
    },
    {
        key: 'bonus',
        labelKey: 'permissions.group.bonus',
        icon: 'RiseOutlined',
        permissions: [
            { value: PERMISSIONS.BONUS.DASHBOARD, labelKey: 'permissions.bonus.dashboard' },
            { value: PERMISSIONS.BONUS.GENERATION, labelKey: 'permissions.bonus.generation' },
            { value: PERMISSIONS.BONUS.LEVEL, labelKey: 'permissions.bonus.level' },
            { value: PERMISSIONS.BONUS.DROPSHIPPING, labelKey: 'permissions.bonus.dropshipping' },
            { value: PERMISSIONS.BONUS.RESTORE, labelKey: 'permissions.bonus.restore' },
            { value: PERMISSIONS.BONUS.COACHING, labelKey: 'permissions.bonus.coaching' },
            { value: PERMISSIONS.BONUS.EXPORT, labelKey: 'permissions.bonus.export' },
        ]
    },
    {
        key: 'reglement',
        labelKey: 'permissions.group.reglement',
        icon: 'FileDoneOutlined',
        permissions: [
            { value: PERMISSIONS.REGLEMENT.VIEW, labelKey: 'permissions.reglement.view' },
            { value: PERMISSIONS.REGLEMENT.CREATE, labelKey: 'permissions.reglement.create' },
            { value: PERMISSIONS.REGLEMENT.EXPORT, labelKey: 'permissions.reglement.export' },
            { value: PERMISSIONS.REGLEMENT.DOWNLOAD, labelKey: 'permissions.reglement.download' },
        ]
    },
    {
        key: 'media',
        labelKey: 'permissions.group.media',
        icon: 'PictureOutlined',
        permissions: [
            { value: PERMISSIONS.MEDIA.VIEW, labelKey: 'permissions.media.view' },
            { value: PERMISSIONS.MEDIA.UPLOAD, labelKey: 'permissions.media.upload' },
            { value: PERMISSIONS.MEDIA.CATALOG_UPDATER, labelKey: 'permissions.media.catalog_updater' },
            { value: PERMISSIONS.MEDIA.FILES_MANAGE, labelKey: 'permissions.media.files_manage' },
            { value: PERMISSIONS.MEDIA.DELETE, labelKey: 'permissions.media.delete' },
        ]
    },
    {
        key: 'countries_settings',
        labelKey: 'permissions.group.countries_settings',
        icon: 'GlobalOutlined',
        permissions: [
            { value: PERMISSIONS.COUNTRIES_SETTINGS.VIEW, labelKey: 'permissions.countries_settings.view' },
            { value: PERMISSIONS.COUNTRIES_SETTINGS.CREATE, labelKey: 'permissions.countries_settings.create' },
            { value: PERMISSIONS.COUNTRIES_SETTINGS.EDIT, labelKey: 'permissions.countries_settings.edit' },
            { value: PERMISSIONS.COUNTRIES_SETTINGS.MANAGE_TAXES, labelKey: 'permissions.countries_settings.manage_taxes' },
            { value: PERMISSIONS.COUNTRIES_SETTINGS.MANAGE_CURRENCY, labelKey: 'permissions.countries_settings.manage_currency' },
        ]
    },
    {
        key: 'international',
        labelKey: 'permissions.group.international',
        icon: 'TranslationOutlined',
        permissions: [
            { value: PERMISSIONS.INTERNATIONAL.CURRENCY_VIEW, labelKey: 'permissions.international.currency_view' },
            { value: PERMISSIONS.INTERNATIONAL.CURRENCY_MANAGE, labelKey: 'permissions.international.currency_manage' },
            { value: PERMISSIONS.INTERNATIONAL.LANGUAGE_VIEW, labelKey: 'permissions.international.language_view' },
            { value: PERMISSIONS.INTERNATIONAL.LANGUAGE_MANAGE, labelKey: 'permissions.international.language_manage' },
        ]
    },
    {
        key: 'settings',
        labelKey: 'permissions.group.settings',
        icon: 'SettingOutlined',
        permissions: [
            { value: PERMISSIONS.SETTINGS.VIEW, labelKey: 'permissions.settings.view' },
            { value: PERMISSIONS.SETTINGS.EDIT, labelKey: 'permissions.settings.edit' },
            { value: PERMISSIONS.SETTINGS.SYSTEM, labelKey: 'permissions.settings.system' },
        ]
    },
];

// ============================================
// ROLE PRESETS (Default permissions per role)
// ============================================
export const ROLE_PRESETS = {
    [ROLES.MASTER]: {
        // Full access - all permissions
        getAllPermissions: () => {
            const allPerms = [PERMISSIONS.COUNTRY.ALL];
            Object.values(PERMISSIONS).forEach(module => {
                if (typeof module === 'object') {
                    Object.values(module).forEach(perm => {
                        if (typeof perm === 'string') {
                            allPerms.push(perm);
                        }
                    });
                }
            });
            return allPerms;
        }
    },
    [ROLES.SUPERVISOR]: {
        permissions: [
            // Dashboard
            PERMISSIONS.DASHBOARD.VIEW,
            PERMISSIONS.DASHBOARD.SALES,
            // Users
            PERMISSIONS.USERS.VIEW,
            PERMISSIONS.USERS.EDIT,
            PERMISSIONS.USERS.BLOCK,
            PERMISSIONS.USERS.VIEW_DETAILS,
            // Admins (limited)
            PERMISSIONS.ADMINS.VIEW,
            PERMISSIONS.ADMINS.CREATE,
            PERMISSIONS.ADMINS.EDIT,
            // Products
            ...Object.values(PERMISSIONS.PRODUCTS),
            // Categories
            ...Object.values(PERMISSIONS.CATEGORIES),
            // Orders
            ...Object.values(PERMISSIONS.ORDERS),
            // Invoices
            ...Object.values(PERMISSIONS.INVOICES),
            // Finance
            PERMISSIONS.FINANCE.VIEW,
            PERMISSIONS.FINANCE.VIEW_TOTALS,
            // Payments
            PERMISSIONS.PAYMENTS.VIEW,
            PERMISSIONS.PAYMENTS.VIEW_DETAILS,
        ]
    },
    [ROLES.PRODUCT]: {
        permissions: [
            PERMISSIONS.DASHBOARD.VIEW,
            ...Object.values(PERMISSIONS.PRODUCTS),
            ...Object.values(PERMISSIONS.CATEGORIES),
            ...Object.values(PERMISSIONS.DISCOUNTS),
            PERMISSIONS.MEDIA.VIEW,
            PERMISSIONS.MEDIA.UPLOAD,
        ]
    },
    [ROLES.FINANCIER]: {
        permissions: [
            PERMISSIONS.DASHBOARD.VIEW,
            PERMISSIONS.DASHBOARD.SALES,
            ...Object.values(PERMISSIONS.FINANCE),
            ...Object.values(PERMISSIONS.PAYMENTS),
            PERMISSIONS.INVOICES.VIEW,
            PERMISSIONS.INVOICES.EXPORT,
            PERMISSIONS.INVOICES.EXPORT_FACTURE,
            ...Object.values(PERMISSIONS.REGLEMENT),
            ...Object.values(PERMISSIONS.BONUS),
        ]
    },
    [ROLES.ORDER]: {
        permissions: [
            PERMISSIONS.DASHBOARD.VIEW,
            ...Object.values(PERMISSIONS.ORDERS),
            PERMISSIONS.INVOICES.VIEW,
            PERMISSIONS.INVOICES.CHANGE_STATUS,
            PERMISSIONS.INVOICES.EXPORT,
            PERMISSIONS.USERS.VIEW,
            PERMISSIONS.USERS.VIEW_DETAILS,
            ...Object.values(PERMISSIONS.CARRIERS),
        ]
    },
    [ROLES.MEDIA]: {
        permissions: [
            PERMISSIONS.DASHBOARD.VIEW,
            ...Object.values(PERMISSIONS.MEDIA),
            PERMISSIONS.PRODUCTS.VIEW,
        ]
    },
    [ROLES.DIGITAL]: {
        permissions: [
            PERMISSIONS.DASHBOARD.VIEW,
            ...Object.values(PERMISSIONS.FORMATIONS),
            PERMISSIONS.MEDIA.VIEW,
            PERMISSIONS.MEDIA.UPLOAD,
        ]
    },
    [ROLES.TRAINING]: {
        permissions: [
            PERMISSIONS.DASHBOARD.VIEW,
            ...Object.values(PERMISSIONS.FORMATIONS),
            ...Object.values(PERMISSIONS.SURVEYS),
            ...Object.values(PERMISSIONS.CHALLENGES),
        ]
    },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get all permissions for a given role
 * @param {string} role - The role (e.g., ROLE_MASTER)
 * @returns {string[]} Array of permission strings
 */
export const getPermissionsForRole = (role) => {
    const preset = ROLE_PRESETS[role];
    if (!preset) return [];

    if (preset.getAllPermissions) {
        return preset.getAllPermissions();
    }
    return preset.permissions || [];
};

/**
 * Generate country permission string
 * @param {string} isoCode - Country ISO code (e.g., 'TN')
 * @returns {string} Permission string (e.g., 'ROLE_COUNTRY_TN')
 */
export const getCountryPermission = (isoCode) => {
    return `ROLE_COUNTRY_${isoCode.toUpperCase()}`;
};

/**
 * Check if user has a specific permission
 * @param {string[]} userPermissions - User's permissions array
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (userPermissions, permission) => {
    if (!userPermissions || !Array.isArray(userPermissions)) return false;
    // ROLE_MASTER or ROLE_COUNTRY_ALL grants all access
    if (userPermissions.includes(ROLES.MASTER) || userPermissions.includes(PERMISSIONS.COUNTRY.ALL)) {
        return true;
    }
    return userPermissions.includes(permission);
};

/**
 * Check if user has access to a specific country
 * @param {string[]} userPermissions - User's permissions array
 * @param {string} countryIsoCode - Country ISO code
 * @returns {boolean}
 */
export const hasCountryAccess = (userPermissions, countryIsoCode) => {
    if (!userPermissions || !Array.isArray(userPermissions)) return false;
    if (userPermissions.includes(PERMISSIONS.COUNTRY.ALL)) return true;
    return userPermissions.includes(getCountryPermission(countryIsoCode));
};

/**
 * Get all flat permissions for sending to backend
 * @returns {string[]} Array of all permission values
 */
export const getAllPermissionValues = () => {
    const allPerms = [];
    Object.values(PERMISSIONS).forEach(module => {
        if (typeof module === 'object') {
            Object.values(module).forEach(perm => {
                if (typeof perm === 'string') {
                    allPerms.push(perm);
                }
            });
        }
    });
    return allPerms;
};

export default {
    ROLES,
    ROLE_HIERARCHY,
    PERMISSIONS,
    ROLE_GROUPS,
    ROLE_PRESETS,
    getPermissionsForRole,
    getCountryPermission,
    hasPermission,
    hasCountryAccess,
    getAllPermissionValues,
};

